<template>
  <div class="month-end-container">
    <!-- SIDEBAR -->
    <Sidebar @open-contacts="goToEmailContacts" />

    <!-- MAIN -->
    <main class="main">
      <!-- TOPBAR -->
      <div class="topbar">
        <div class="topbar-left">
          <h1 class="page-title">Month-end</h1>
          <!-- Period Selector (visible pour naviguer entre les mois) -->
          <div class="period-selector">
            <select v-model="selectedMonth" @change="fetchReports" class="period-select">
              <option v-for="m in months" :key="m.value" :value="m.value">
                {{ m.label }}
              </option>
            </select>
            <select v-model="selectedYear" @change="fetchReports" class="period-select">
              <option v-for="y in years" :key="y" :value="y">{{ y }}</option>
            </select>
          </div>
        </div>
        <div class="topbar-right">
          <span class="welcome-text">Welcome back!</span>
          <button class="btn-logout" @click="logout">Logout</button>
        </div>
      </div>

      <!-- CONTENT -->
      <div class="content">
    <!-- Progress Card avec stats intégrées -->
    <div class="progress-card-modern">
      <div class="progress-header">
        <div class="progress-title">Progress — {{ months.find(m => m.value === selectedMonth)?.label }} {{ selectedYear }}</div>
      </div>

      <div class="progress-content">
        <!-- GAUCHE: Progress bar -->
        <div class="progress-left">
          <div class="progress-bar-wrapper">
            <div class="progress-bar-track">
              <div
                class="progress-bar-fill"
                :style="{ width: stats.progress_percent + '%' }"
              ></div>
            </div>
          </div>
          <div class="progress-info-text">
            {{ stats.sent + stats.pushed_to_xero }} of {{ stats.total }} reports sent · {{ Math.round(stats.progress_percent) }}% complete
          </div>
        </div>

        <!-- DROITE: Stats en ligne -->
        <div class="progress-right">
          <div class="stat-item stat-to-send">
            <div class="stat-number">{{ stats.ready_to_send }}</div>
            <div class="stat-label">To send</div>
          </div>
          <div class="stat-item stat-sent">
            <div class="stat-number">{{ stats.sent + stats.pushed_to_xero }}</div>
            <div class="stat-label">Sent</div>
          </div>
          <div class="stat-item stat-total">
            <div class="stat-number">{{ stats.total }}</div>
            <div class="stat-label">Total</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Filters Pills + Action Buttons sur la même ligne -->
    <div class="filters-actions-row">
      <div class="filters-pills">
        <button
          @click="changeFilter('all')"
          :class="{ active: currentFilter === 'all' }"
          class="pill-btn"
        >
          All
        </button>
        <button
          @click="changeFilter('ready_to_send')"
          :class="{ active: currentFilter === 'ready_to_send' }"
          class="pill-btn"
        >
          To send
        </button>
        <button
          @click="changeFilter('sent')"
          :class="{ active: currentFilter === 'sent' }"
          class="pill-btn"
        >
          Sent
        </button>
      </div>

      <div class="action-buttons-row">
        <button
          @click="sendAllUnsent"
          class="btn-send-all"
          :disabled="stats.ready_to_send === 0 || sending"
        >
          Send all unsent ({{ stats.ready_to_send }})
        </button>

        <button
          @click="pushToXero"
          class="btn-push-xero"
          :disabled="stats.sent === 0 || pushing"
        >
          Push to Xero
        </button>
      </div>
    </div>

    <!-- Main Table Card -->
    <div class="table-card-modern">
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>Loading reports...</p>
      </div>

      <table v-else-if="reports.length > 0" class="reports-table-modern">
        <thead>
          <tr>
            <th>PROPERTY</th>
            <th>MANAGEMENT FEE</th>
            <th>CLEANING</th>
            <th>TOTAL EX VAT</th>
            <th>STATUS</th>
            <th>ACTION</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="report in reports" :key="report.id">
            <td>
              <div class="property-info">
                <div class="property-name">{{ report.property_name }}</div>
                <div class="owner-name-gray">{{ report.owner_name || 'N/A' }}</div>
              </div>
            </td>
            <td>
              <div class="fee-amount">£{{ parseFloat(report.management_fee || 0).toFixed(0) }}</div>
            </td>
            <td>
              <div class="cleaning-amount">
                <div>£{{ parseFloat(report.cleaning_fees || 0).toFixed(0) }}</div>
                <div class="inc-vat-text">inc VAT</div>
              </div>
            </td>
            <td>
              <div class="total-ex-vat">
                <div class="total-main">£{{ parseFloat((parseFloat(report.management_fee || 0) + parseFloat(report.cleaning_fees || 0))).toFixed(0) }}</div>
                <div class="vat-addition">+£{{ parseFloat(report.vat_amount || 0).toFixed(0) }} VAT</div>
              </div>
            </td>
            <td>
              <span
                class="status-badge"
                :class="getStatusBadgeClass(report.status)"
              >
                {{ getStatusLabel(report.status) }}
              </span>
            </td>
            <td>
              <div v-if="report.status === 'ready_to_send'" class="action-buttons">
                <button
                  @click="openSendModal(report)"
                  class="btn-send-action"
                  :disabled="sending"
                >
                  Send
                </button>
                <button
                  @click="openUnlockModal(report)"
                  class="btn-unlock-action"
                  :disabled="unlocking"
                >
                  🔓 Unlock
                </button>
              </div>
              <span v-else-if="report.status === 'sent'" class="checkmark-icon">✓</span>
              <span v-else-if="report.status === 'pushed_to_xero'" class="checkmark-icon">✓</span>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-else class="empty-state">
        <p>No reports found for {{ months.find(m => m.value === selectedMonth)?.label }} {{ selectedYear }}</p>
        <router-link to="/reservations" class="link-primary">Go to Reservations to create reports</router-link>
      </div>
    </div>

    <!-- Modal Send Report -->
    <div v-if="showSendModal" class="modal-overlay" @click.self="closeSendModal">
      <div class="modal-send-report">
        <div class="modal-header-gradient">
          <div class="modal-icon-circle">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
              <rect x="2" y="4" width="20" height="16" rx="2"/>
              <path d="M2 8l10 6 10-6"/>
            </svg>
          </div>
          <h3 class="modal-title">Send report by email</h3>
          <p class="modal-subtitle">{{ selectedReport?.property_name }} — {{ months.find(m => m.value === selectedMonth)?.label }} {{ selectedYear }}</p>
        </div>

        <div class="modal-body">
          <div class="sending-to-section">
            <div class="sending-to-label">SENDING TO</div>
            <div class="recipient-card">
              <div class="avatar-circle">{{ getInitials(selectedReport?.owner_name) }}</div>
              <div class="recipient-info">
                <div class="recipient-name">{{ selectedReport?.owner_name }}</div>
                <div class="recipient-email">{{ selectedReport?.owner_email }}</div>
              </div>
            </div>
          </div>

          <div class="warning-message">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#d97706" stroke-width="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <span>This report will be sent immediately to the owner's email address</span>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="closeSendModal" class="btn-modal-cancel">Cancel</button>
          <button @click="confirmSendReport" class="btn-modal-send" :disabled="sending">
            <span v-if="!sending">Send report</span>
            <span v-else>Sending...</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Modal Success Xero -->
    <div v-if="showSuccessModal" class="modal-overlay" @click.self="showSuccessModal = false">
      <div class="modal-success">
        <div class="modal-header-success">
          <div class="checkmark-circle">
            <svg class="checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
              <circle class="checkmark-circle-bg" cx="26" cy="26" r="25" fill="none"/>
              <path class="checkmark-check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8"/>
            </svg>
          </div>
          <h3 class="modal-title-success">{{ successMessage }}</h3>
        </div>

        <div class="modal-body-success">
          <div v-if="successDetails.length > 0" class="success-details">
            <div
              v-for="(detail, index) in successDetails"
              :key="index"
              :class="['detail-item', detail.type === 'error' ? 'detail-error' : 'detail-success']"
            >
              <span v-if="detail.type === 'success'" class="detail-icon">✓</span>
              <span v-else class="detail-icon">✗</span>
              <span class="detail-text">{{ detail.text }}</span>
            </div>
          </div>
        </div>

        <div class="modal-footer-success">
          <button @click="showSuccessModal = false" class="btn-modal-ok">OK</button>
        </div>
      </div>
    </div>

    <!-- Modal Unlock Report -->
    <div v-if="showUnlockModal" class="modal-overlay" @click.self="closeUnlockModal">
      <div class="modal-unlock-report">
        <div class="modal-header-warning">
          <div class="modal-icon-circle warning">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
          </div>
          <h3 class="modal-title">⚠️ Unlock Report</h3>
        </div>

        <div class="modal-body">
          <div class="unlock-details">
            <div class="unlock-info-row">
              <span class="unlock-label">Property:</span>
              <span class="unlock-value">{{ selectedReport?.property_name }}</span>
            </div>
            <div class="unlock-info-row">
              <span class="unlock-label">Period:</span>
              <span class="unlock-value">{{ months.find(m => m.value === selectedMonth)?.label }} {{ selectedYear }}</span>
            </div>
          </div>

          <div class="warning-message-unlock">
            <p><strong>Are you sure you want to unlock this report?</strong></p>
            <p class="warning-subtext">The report will return to Draft status and you'll need to lock it again before sending.</p>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="closeUnlockModal" class="btn-modal-cancel">Cancel</button>
          <button @click="confirmUnlock" class="btn-modal-unlock" :disabled="unlocking">
            <span v-if="!unlocking">Unlock</span>
            <span v-else>Unlocking...</span>
          </button>
        </div>
      </div>
    </div>
      </div>
    </main>
  </div>
</template>

<script>
import axios from 'axios';
import Sidebar from '@/components/Sidebar.vue';

export default {
  name: 'MonthEndView',
  components: {
    Sidebar
  },
  data() {
    return {
      selectedMonth: new Date().getMonth() + 1,
      selectedYear: new Date().getFullYear(),
      currentFilter: 'all',
      reports: [],
      stats: {
        total: 0,
        ready_to_send: 0,
        sent: 0,
        pushed_to_xero: 0,
        progress_percent: 0
      },
      months: [
        { value: 1, label: 'January' },
        { value: 2, label: 'February' },
        { value: 3, label: 'March' },
        { value: 4, label: 'April' },
        { value: 5, label: 'May' },
        { value: 6, label: 'June' },
        { value: 7, label: 'July' },
        { value: 8, label: 'August' },
        { value: 9, label: 'September' },
        { value: 10, label: 'October' },
        { value: 11, label: 'November' },
        { value: 12, label: 'December' }
      ],
      years: [],
      loading: false,
      sending: false,
      pushing: false,
      unlocking: false,
      showSendModal: false,
      showUnlockModal: false,
      selectedReport: null,
      showSuccessModal: false,
      successMessage: '',
      successDetails: []
    };
  },

  computed: {
    baseUrl() {
      // Use same logic as settings.js for consistency
      if (window.location.pathname.startsWith('/dev')) {
        return window.location.origin + '/dev';
      }
      return window.location.origin;
    }
  },

  methods: {
    async fetchReports() {
      this.loading = true;
      try {
        const token = localStorage.getItem('token');

        const response = await axios.get(
          `${this.baseUrl}/api/pms/month-end/`,
          {
            params: {
              year: this.selectedYear,
              month: this.selectedMonth,
              status: this.currentFilter
            },
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );

        if (response.data.success) {
          this.reports = response.data.data.reports;
          this.stats = response.data.data.stats;
        }
      } catch (error) {
        console.error('Error fetching reports:', error);
      } finally {
        this.loading = false;
      }
    },

    changeFilter(filter) {
      this.currentFilter = filter;
      this.fetchReports();
    },

    openSendModal(report) {
      this.selectedReport = report;
      this.showSendModal = true;
    },

    closeSendModal() {
      this.showSendModal = false;
      this.selectedReport = null;
    },

    getInitials(name) {
      if (!name) return 'NA';
      const parts = name.split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    },

    async confirmSendReport() {
      if (!this.selectedReport) return;

      this.sending = true;
      try {
        const token = localStorage.getItem('token');

        const response = await axios.post(
          `${this.baseUrl}/api/pms/monthly-reports/${this.selectedReport.id}/send/`,
          {},
          { headers: { 'Authorization': `Bearer ${token}` } }
        );

        if (response.data.success) {
          alert(`Report sent to ${this.selectedReport.owner_email}`);
          this.closeSendModal();
          this.fetchReports();
        }
      } catch (error) {
        console.error('Error sending report:', error);
        const message = error.response?.data?.message || 'Error sending report';
        alert(message);
      } finally {
        this.sending = false;
      }
    },

    openUnlockModal(report) {
      this.selectedReport = report;
      this.showUnlockModal = true;
    },

    closeUnlockModal() {
      this.showUnlockModal = false;
      this.selectedReport = null;
    },

    async confirmUnlock() {
      if (!this.selectedReport) return;

      this.unlocking = true;
      try {
        const token = localStorage.getItem('token');

        const response = await axios.post(
          `${this.baseUrl}/api/pms/unlock-report/`,
          { report_id: this.selectedReport.id },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );

        if (response.data.success) {
          // Show success message
          this.successMessage = 'Report unlocked successfully!';
          this.successDetails = [{
            type: 'success',
            text: `${this.selectedReport.property_name} returned to Draft status`
          }];
          this.showSuccessModal = true;

          // Close unlock modal and refresh
          this.closeUnlockModal();
          this.fetchReports();
        }
      } catch (error) {
        console.error('Error unlocking report:', error);
        const message = error.response?.data?.message || 'Error unlocking report';
        alert(message);
      } finally {
        this.unlocking = false;
      }
    },

    async sendReport(report) {
      if (!confirm(`Send report to ${report.owner_name}?\n\nEmail: ${report.owner_email}`)) {
        return;
      }

      this.sending = true;
      try {
        const token = localStorage.getItem('token');

        const response = await axios.post(
          `${this.baseUrl}/api/pms/monthly-reports/${report.id}/send/`,
          {},
          { headers: { 'Authorization': `Bearer ${token}` } }
        );

        if (response.data.success) {
          alert(`Report sent to ${report.owner_email}`);
          this.fetchReports();
        }
      } catch (error) {
        console.error('Error sending report:', error);
        const message = error.response?.data?.message || 'Error sending report';
        alert(message);
      } finally {
        this.sending = false;
      }
    },

    async sendAllUnsent() {
      if (!confirm(`Send ${this.stats.ready_to_send} unsent reports?\n\nYou will confirm each one individually.`)) {
        return;
      }

      this.sending = true;
      try {
        const token = localStorage.getItem('token');

        const response = await axios.post(
          `${this.baseUrl}/api/pms/send-all-unsent/`,
          {
            year: this.selectedYear,
            month: this.selectedMonth
          },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );

        if (response.data.success) {
          const unsentReports = response.data.data.unsent_reports;

          if (unsentReports.length === 0) {
            alert('No unsent reports');
            this.sending = false;
            return;
          }

          let sentCount = 0;
          for (const report of unsentReports) {
            const confirmed = confirm(
              `Send report to:\n\n` +
              `Property: ${report.property_name}\n` +
              `Owner: ${report.owner_name}\n` +
              `Email: ${report.owner_email}\n\n` +
              `Continue? (${sentCount + 1}/${unsentReports.length})`
            );

            if (confirmed) {
              try {
                await axios.post(
                  `${this.baseUrl}/api/pms/monthly-reports/${report.id}/send/`,
                  {},
                  { headers: { 'Authorization': `Bearer ${token}` } }
                );
                sentCount++;
              } catch (err) {
                console.error(`Error sending report ${report.id}:`, err);
                alert(`Error sending to ${report.owner_name}: ${err.response?.data?.message || err.message}`);
              }
            }
          }

          alert(`${sentCount} reports sent successfully`);
          this.fetchReports();
        }
      } catch (error) {
        console.error('Error:', error);
        alert('Error processing reports');
      } finally {
        this.sending = false;
      }
    },

    async pushToXero() {
      if (this.stats.sent === 0) return;

      const sentCount = this.stats.sent;
      if (!confirm(`Push ${sentCount} sent report${sentCount > 1 ? 's' : ''} to Xero as draft invoices?\n\nThis will create invoices in Xero for review.`)) {
        return;
      }

      this.pushing = true;

      try {
        const token = localStorage.getItem('token');

        const response = await fetch(`${this.baseUrl}/api/pms/push-to-xero/`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            year: this.selectedYear,
            month: this.selectedMonth
          })
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || 'Failed to push to Xero');
        }

        if (data.success) {
          const { pushed, failed, results, errors } = data.data;

          this.successMessage = `Successfully pushed ${pushed} invoice${pushed > 1 ? 's' : ''} to Xero`;
          this.successDetails = [];

          if (results && results.length > 0) {
            this.successDetails = results.map(r => ({
              type: 'success',
              text: `${r.property_name}: ${r.invoice_number}`
            }));
          }

          if (failed > 0 && errors && errors.length > 0) {
            errors.forEach(err => {
              this.successDetails.push({
                type: 'error',
                text: `${err.property_name}: ${err.error}`
              });
            });
          }

          this.showSuccessModal = true;
          this.fetchReports();
        } else {
          throw new Error(data.message || 'Unknown error');
        }

      } catch (error) {
        console.error('Error pushing to Xero:', error);

        this.successMessage = 'Failed to push to Xero';
        this.successDetails = [{
          type: 'error',
          text: error.message
        }];
        this.showSuccessModal = true;

        if (error.message.includes('not connected') || error.message.includes('expired')) {
          setTimeout(() => {
            this.$router.push('/xero-settings');
          }, 2000);
        }
      } finally {
        this.pushing = false;
      }
    },

    getStatusBadgeClass(status) {
      const classes = {
        'ready_to_send': 'badge-to-send',
        'sent': 'badge-sent',
        'pushed_to_xero': 'badge-sent'
      };
      return classes[status] || 'badge-default';
    },

    getStatusClass(status) {
      const classes = {
        'ready_to_send': 'amber',
        'sent': 'green',
        'pushed_to_xero': 'blue'
      };
      return classes[status] || 'gray';
    },

    getStatusLabel(status) {
      const labels = {
        'ready_to_send': 'To send',
        'sent': 'Sent',
        'pushed_to_xero': 'Sent'
      };
      return labels[status] || status;
    },

    goToEmailContacts() {
      this.$router.push({ path: '/reservations', query: { openContacts: 'true' } });
    },

    logout() {
      localStorage.removeItem('token');
      this.$router.push('/login');
    }
  },

  mounted() {
    const currentYear = new Date().getFullYear();
    this.years = [
      currentYear - 2,
      currentYear - 1,
      currentYear,
      currentYear + 1
    ];

    if (this.$route.query.month) {
      this.selectedMonth = parseInt(this.$route.query.month);
    }
    if (this.$route.query.year) {
      this.selectedYear = parseInt(this.$route.query.year);
    }

    this.fetchReports();
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:wght@400;500;600;700;800&family=DM+Sans:wght@400;500;600;700&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

.month-end-container {
  display: flex;
  min-height: 100vh;
  background: #e6e4f7;
  font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

/* ============= SIDEBAR ============= */
/* Sidebar styles removed - using component Sidebar.vue */

/* ============= MAIN ============= */
.main {
  margin-left: 218px;
  flex: 1;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* ============= TOPBAR ============= */
.topbar {
  background: white;
  border-bottom: 1px solid #ebe9f8;
  padding: 0 28px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: sticky;
  top: 0;
  z-index: 50;
}

.topbar-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.page-title {
  font-family: 'Bricolage Grotesque', sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0;
  letter-spacing: -0.5px;
}

.period-selector {
  display: flex;
  align-items: center;
  gap: 8px;
}

.topbar-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.welcome-text {
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
}

.btn-logout {
  background: #ef4444;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-logout:hover {
  background: #dc2626;
  transform: translateY(-1px);
}

/* ============= CONTENT ============= */
.content {
  padding: 24px 28px;
  flex: 1;
}

/* ============= PERIOD SELECTOR ============= */
.period-select {
  padding: 8px 14px;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  background: white;
  color: #4b5563;
  cursor: pointer;
  transition: all 0.2s;
  font-family: 'DM Sans', sans-serif;
}

.period-select:hover {
  border-color: #8075f0;
}

.period-select:focus {
  outline: none;
  border-color: #8075f0;
  box-shadow: 0 0 0 3px rgba(128, 117, 240, 0.1);
}

/* ============= PROGRESS CARD AVEC STATS ============= */
.progress-card-modern {
  background: white;
  border-radius: 12px;
  padding: 20px 24px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(128, 117, 240, 0.08);
}

.progress-header {
  margin-bottom: 16px;
}

.progress-title {
  font-size: 14px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0;
  font-family: 'Bricolage Grotesque', sans-serif;
}

.progress-content {
  display: flex;
  align-items: center;
  gap: 40px;
}

.progress-left {
  flex: 1;
}

.progress-bar-wrapper {
  margin-bottom: 10px;
}

.progress-bar-track {
  height: 8px;
  background: #e5e7eb;
  border-radius: 6px;
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #8075f0 0%, #9b8ff5 100%);
  border-radius: 6px;
  transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.progress-info-text {
  font-size: 12px;
  color: #9ca3af;
  font-weight: 500;
}

.progress-right {
  display: flex;
  gap: 32px;
  align-items: center;
  justify-content: flex-end;
}

.stat-item {
  text-align: center;
  min-width: 50px;
}

.stat-number {
  font-family: 'Bricolage Grotesque', sans-serif;
  font-size: 32px;
  font-weight: 800;
  line-height: 1;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 10px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: capitalize;
  letter-spacing: 0;
}

.stat-to-send .stat-number {
  color: #f59e0b;
}

.stat-sent .stat-number {
  color: #10b981;
}

.stat-total .stat-number {
  color: #9ca3af;
}

/* ============= FILTERS + ACTIONS SUR MÊME LIGNE ============= */
.filters-actions-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 18px;
  gap: 20px;
}

/* ============= FILTERS PILLS ============= */
.filters-pills {
  display: flex;
  gap: 8px;
}

.pill-btn {
  padding: 6px 16px;
  border: 1.5px solid #e5e7eb;
  background: white;
  border-radius: 50px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  color: #4b5563;
  transition: all 0.2s;
  font-family: 'DM Sans', sans-serif;
}

.pill-btn:hover {
  border-color: #8075f0;
  color: #8075f0;
}

.pill-btn.active {
  background: #8075f0;
  color: white;
  border-color: #8075f0;
}

/* ============= ACTION BUTTONS ============= */
.action-buttons-row {
  display: flex;
  gap: 8px;
}

.btn-send-all {
  background: #fbbf24;
  color: #78350f;
  border: none;
  padding: 7px 18px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  font-family: 'DM Sans', sans-serif;
}

.btn-send-all:hover:not(:disabled) {
  background: #f59e0b;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(251, 191, 36, 0.3);
}

.btn-send-all:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-push-xero {
  background: #8075f0;
  color: white;
  border: none;
  padding: 7px 18px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  font-family: 'DM Sans', sans-serif;
}

.btn-push-xero:hover:not(:disabled) {
  background: #6f63e6;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(128, 117, 240, 0.3);
}

.btn-push-xero:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ============= TABLE CARD ============= */
.table-card-modern {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(128, 117, 240, 0.08);
  overflow-x: auto;
}

.reports-table-modern {
  width: 100%;
  border-collapse: collapse;
}

.reports-table-modern thead th {
  text-align: left;
  padding: 12px 12px 12px 0;
  font-size: 10px;
  font-weight: 700;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  border-bottom: 1.5px solid #f3f4f6;
  font-family: 'DM Sans', sans-serif;
}

.reports-table-modern tbody td {
  padding: 14px 12px 14px 0;
  border-bottom: 1px solid #f3f4f6;
  font-size: 14px;
  color: #1f2937;
}

.property-info {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.property-name {
  font-weight: 600;
  color: #111827;
  font-size: 14px;
}

.owner-name-gray {
  font-size: 12px;
  color: #9ca3af;
  font-weight: 500;
}

.fee-amount {
  font-weight: 600;
  color: #111827;
  font-size: 14px;
}

.cleaning-amount {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.inc-vat-text {
  font-size: 11px;
  color: #9ca3af;
  font-weight: 500;
}

.total-ex-vat {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.total-main {
  font-weight: 700;
  color: #111827;
  font-size: 14px;
}

.vat-addition {
  font-size: 11px;
  color: #9ca3af;
  font-weight: 500;
}

/* ============= STATUS BADGES ============= */
.status-badge {
  display: inline-block;
  padding: 5px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  border: 1.5px solid;
}

.badge-to-send {
  background: #fffbeb;
  color: #d97706;
  border-color: #fde68a;
}

.badge-sent {
  background: #ecfdf5;
  color: #059669;
  border-color: #a7f3d0;
}

.badge-default {
  background: #f3f4f6;
  color: #6b7280;
  border-color: #e5e7eb;
}

/* ============= ACTION BUTTONS (table) ============= */
.btn-send-action {
  background: #8075f0;
  color: white;
  border: none;
  padding: 6px 16px;
  border-radius: 7px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  transition: all 0.2s;
  font-family: 'DM Sans', sans-serif;
}

.btn-send-action:hover:not(:disabled) {
  background: #6f63e6;
  transform: translateY(-1px);
}

.btn-send-action:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.action-buttons {
  display: flex;
  gap: 8px;
  align-items: center;
}

.btn-unlock-action {
  background: #f59e0b;
  color: white;
  border: none;
  padding: 6px 14px;
  border-radius: 7px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  transition: all 0.2s;
  font-family: 'DM Sans', sans-serif;
}

.btn-unlock-action:hover:not(:disabled) {
  background: #d97706;
  transform: translateY(-1px);
}

.btn-unlock-action:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.checkmark-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  background: #10b981;
  color: white;
  border-radius: 50%;
  font-weight: 700;
  font-size: 14px;
}

/* ============= LOADING & EMPTY ============= */
.loading-state {
  text-align: center;
  padding: 80px 20px;
  color: #6b7280;
}

.spinner {
  border: 3px solid #f3f4f6;
  border-top: 3px solid #8075f0;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
}

.link-primary {
  display: inline-block;
  margin-top: 16px;
  color: #8075f0;
  text-decoration: none;
  font-weight: 600;
  font-size: 15px;
}

.link-primary:hover {
  text-decoration: underline;
}

/* ============= MODAL SEND REPORT ============= */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.modal-send-report {
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 440px;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
}

.modal-header-gradient {
  background: linear-gradient(135deg, #8075f0 0%, #9b8ff5 100%);
  padding: 28px 24px;
  text-align: center;
  color: white;
}

.modal-icon-circle {
  width: 52px;
  height: 52px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 14px;
}

.modal-icon-circle svg {
  width: 20px;
  height: 20px;
}

.modal-title {
  font-family: 'Bricolage Grotesque', sans-serif;
  font-size: 19px;
  font-weight: 700;
  margin: 0 0 6px 0;
}

.modal-subtitle {
  font-size: 13px;
  margin: 0;
  opacity: 0.9;
  font-weight: 500;
}

.modal-body {
  padding: 24px;
}

.sending-to-section {
  margin-bottom: 18px;
}

.sending-to-label {
  font-size: 10px;
  font-weight: 700;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 10px;
}

.recipient-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 10px;
}

.avatar-circle {
  width: 40px;
  height: 40px;
  background: #8075f0;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
  flex-shrink: 0;
}

.recipient-info {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.recipient-name {
  font-weight: 600;
  color: #111827;
  font-size: 14px;
}

.recipient-email {
  font-size: 13px;
  color: #6b7280;
}

.warning-message {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  background: #fffbeb;
  border: 1.5px solid #fde68a;
  border-radius: 10px;
  font-size: 13px;
  color: #92400e;
  font-weight: 500;
}

.warning-message svg {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
}

.modal-footer {
  padding: 18px 24px;
  border-top: 1px solid #f3f4f6;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

.btn-modal-cancel {
  padding: 10px 20px;
  border: 1.5px solid #e5e7eb;
  background: white;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  color: #4b5563;
  transition: all 0.2s;
  font-family: 'DM Sans', sans-serif;
}

.btn-modal-cancel:hover {
  border-color: #d1d5db;
  background: #f9fafb;
}

.btn-modal-send {
  padding: 10px 24px;
  border: none;
  background: #8075f0;
  color: white;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  font-family: 'DM Sans', sans-serif;
}

.btn-modal-send:hover:not(:disabled) {
  background: #6f63e6;
  transform: translateY(-1px);
}

.btn-modal-send:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* ============= RESPONSIVE ============= */
@media (max-width: 1024px) {
  .content {
    padding: 20px;
  }

  .page-title {
    font-size: 18px;
  }

  .progress-content {
    gap: 24px;
  }

  .progress-right {
    gap: 24px;
  }

  .stat-number {
    font-size: 36px;
  }
}

@media (max-width: 768px) {
  .main {
    margin-left: 0;
  }

  .sidebar {
    transform: translateX(-100%);
  }

  .topbar {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    height: auto;
    padding: 16px 20px;
  }

  .progress-content {
    flex-direction: column;
    gap: 20px;
  }

  .progress-left {
    max-width: 100%;
  }

  .progress-right {
    width: 100%;
    justify-content: space-around;
  }

  .filters-actions-row {
    flex-direction: column;
    align-items: stretch;
  }

  .filters-pills {
    justify-content: center;
  }

  .action-buttons-row {
    flex-direction: column;
  }

  .table-card-modern {
    padding: 16px;
    overflow-x: scroll;
  }
}

/* ============= MODAL SUCCESS XERO ============= */
.modal-success {
  background: white;
  border-radius: 16px;
  max-width: 520px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0,0,0,0.3);
  animation: slideUp 0.3s ease-out;
}

.modal-header-success {
  padding: 40px 32px 24px;
  text-align: center;
  border-bottom: 1px solid #f3f4f6;
}

.checkmark-circle {
  width: 80px;
  height: 80px;
  margin: 0 auto 20px;
  position: relative;
}

.checkmark {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: block;
  stroke-width: 3;
  stroke: #10b981;
  stroke-miterlimit: 10;
  box-shadow: inset 0px 0px 0px #10b981;
  animation: fill 0.4s ease-in-out 0.4s forwards, scale 0.3s ease-in-out 0.9s both;
}

.checkmark-circle-bg {
  stroke-dasharray: 166;
  stroke-dashoffset: 166;
  stroke-width: 3;
  stroke-miterlimit: 10;
  stroke: #10b981;
  fill: none;
  animation: stroke 0.6s cubic-bezier(0.65, 0, 0.45, 1) forwards;
}

.checkmark-check {
  transform-origin: 50% 50%;
  stroke-dasharray: 48;
  stroke-dashoffset: 48;
  stroke: #10b981;
  stroke-width: 3;
  animation: stroke 0.3s cubic-bezier(0.65, 0, 0.45, 1) 0.8s forwards;
}

@keyframes stroke {
  100% {
    stroke-dashoffset: 0;
  }
}

@keyframes scale {
  0%, 100% {
    transform: none;
  }
  50% {
    transform: scale3d(1.1, 1.1, 1);
  }
}

@keyframes fill {
  100% {
    box-shadow: inset 0px 0px 0px 30px #10b981;
  }
}

.modal-title-success {
  font-size: 22px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
  font-family: 'DM Sans', sans-serif;
}

.modal-body-success {
  padding: 24px 32px;
  max-height: 400px;
  overflow-y: auto;
}

.success-details {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.detail-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 8px;
  background: #f9fafb;
  font-size: 14px;
}

.detail-success {
  background: #ecfdf5;
  border-left: 3px solid #10b981;
}

.detail-error {
  background: #fef2f2;
  border-left: 3px solid #ef4444;
}

.detail-icon {
  font-weight: bold;
  font-size: 16px;
  flex-shrink: 0;
}

.detail-success .detail-icon {
  color: #10b981;
}

.detail-error .detail-icon {
  color: #ef4444;
}

.detail-text {
  color: #4b5563;
  line-height: 1.5;
  font-family: 'DM Sans', sans-serif;
}

.modal-footer-success {
  padding: 20px 32px;
  border-top: 1px solid #f3f4f6;
  display: flex;
  justify-content: center;
}

.btn-modal-ok {
  padding: 12px 32px;
  border: none;
  background: #8075f0;
  color: white;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  font-family: 'DM Sans', sans-serif;
  min-width: 120px;
}

.btn-modal-ok:hover {
  background: #6d61e0;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(128, 117, 240, 0.3);
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ============= UNLOCK MODAL ============= */
.modal-unlock-report {
  background: white;
  border-radius: 16px;
  max-width: 480px;
  width: 90%;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  animation: slideUp 0.3s ease-out;
}

.modal-header-warning {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  padding: 28px 28px 24px;
  text-align: center;
  position: relative;
}

.modal-icon-circle.warning {
  width: 56px;
  height: 56px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  backdrop-filter: blur(10px);
}

.unlock-details {
  background: #f9fafb;
  padding: 16px;
  border-radius: 10px;
  margin-bottom: 20px;
}

.unlock-info-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
}

.unlock-info-row:not(:last-child) {
  border-bottom: 1px solid #e5e7eb;
}

.unlock-label {
  font-size: 13px;
  color: #6b7280;
  font-weight: 600;
}

.unlock-value {
  font-size: 14px;
  color: #1f2937;
  font-weight: 600;
}

.warning-message-unlock {
  text-align: center;
  padding: 0 12px;
}

.warning-message-unlock p {
  font-size: 14px;
  line-height: 1.6;
  color: #374151;
  margin-bottom: 8px;
}

.warning-message-unlock strong {
  font-weight: 700;
  color: #111827;
}

.warning-subtext {
  font-size: 13px !important;
  color: #6b7280 !important;
}

.btn-modal-unlock {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
  border: none;
  padding: 11px 26px;
  border-radius: 9px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(245, 158, 11, 0.3);
  font-family: 'DM Sans', sans-serif;
}

.btn-modal-unlock:hover:not(:disabled) {
  background: linear-gradient(135deg, #d97706 0%, #b45309 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.4);
}

.btn-modal-unlock:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}
</style>
